// 파트너사 기술 분류 체계 — 27코드 (사용자 제공 24코드 2026-10-27 + PIC-VOICE · SEN-SAFE · SW-AI 2026-11-02).
//   미팅 추출이 설비·시스템마다 이 코드를 달고, 파트너는 같은 코드로 미팅과 매칭된다 —
//   **코드가 조인 키**라 한번 데이터에 실리면 바꾸지 않는다. 이름·설명은 자유롭게 고쳐도 된다.
//
// 저장: ref_partner_contracts.tech_codes (text[], 20261027 마이그레이션).
//   「주요 기술」(main_technologies, 자유 입력)은 역량 가중치·추천이 키로 쓰고 있어 그대로 둔다 — 별개 칸이다.
// ★ 서버(api/sales-handler.js PARTNER_TECH_CODES)와 마이그레이션의 매핑이 이 파일과 같아야 한다.

export type PartnerTechGroup = { key: string; label: string; labelEn: string };
export type PartnerTechCode = { code: string; name: string; description: string; group: string };

export const PARTNER_TECH_GROUPS: PartnerTechGroup[] = [
  { key: "STO", label: "자동 보관", labelEn: "Automated storage" },
  { key: "MOV", label: "자동 이송", labelEn: "Automated transport" },
  { key: "SOR", label: "자동 분류", labelEn: "Sortation" },
  { key: "PIC", label: "피킹·핸들링", labelEn: "Picking · handling" },
  { key: "PAC", label: "포장·인식", labelEn: "Packing · identification" },
  { key: "SW", label: "물류 소프트웨어", labelEn: "Logistics software" },
  { key: "SERV", label: "서비스·기술", labelEn: "Services · technology" },
];

export const PARTNER_TECH_CODES: PartnerTechCode[] = [
  { group: "STO", code: "STO-ASRS", name: "AS/RS", description: "랙과 자동 입출고 장치를 활용해 상품 또는 팔레트를 자동 보관·인출하는 시스템." },
  { group: "STO", code: "STO-SHUT", name: "Shuttle", description: "랙 내부의 셔틀 장치가 상품 또는 보관 용기를 이동시켜 자동 입출고하는 시스템." },
  { group: "STO", code: "STO-VLM", name: "VLM", description: "수직 공간을 활용해 트레이를 자동 보관하고 작업자 위치로 가져오는 수직 리프트형 설비." },
  { group: "STO", code: "STO-AUTO", name: "AutoStore", description: "격자형 보관 구조와 상부 로봇을 이용해 빈을 자동 보관·인출하는 시스템." },
  { group: "MOV", code: "MOV-AMR", name: "AMR", description: "고정 경로 없이 자율주행하며 상품·박스·랙 등을 현장 내에서 운반하는 이동 로봇." },
  { group: "MOV", code: "MOV-AGV", name: "AGV", description: "사전에 설정된 경로나 유도 방식을 따라 물품을 자동 운반하는 무인 운반차." },
  { group: "MOV", code: "MOV-CONV", name: "Conveyor", description: "벨트·롤러 등 연속 이송 장치를 이용해 상품이나 박스를 공정 간 이동시키는 설비." },
  { group: "MOV", code: "MOV-DEL", name: "자율배송·특수이동로봇", description: "실내외 배송, 차량형 자율운송, 재고조사 등 특수 목적의 이동 로봇. 세부 용도를 별도 기록." },
  { group: "SOR", code: "SOR-SORT", name: "Sorter", description: "상품이나 박스를 목적지·주문·배송지 등의 기준에 따라 자동 분류하는 설비. Mini Sorter 포함." },
  { group: "PIC", code: "PIC-DPSDAS", name: "DPS / DAS / DPC", description: "디지털 표시 또는 피킹 카트를 활용해 상품 피킹·분배 작업을 지원하는 시스템." },
  { group: "PIC", code: "PIC-ROBOT", name: "Robot Picking", description: "로봇 암과 그리퍼 등을 이용해 상품을 자동 인식·집어 옮기는 피킹 설비." },
  // 2026-11-02 개정 — 시트에서 「24개 밖」이던 Voice Picking(한국하니웰 주력)을 코드로 올림(사용자 확정).
  { group: "PIC", code: "PIC-VOICE", name: "Voice Picking", description: "작업자가 헤드셋·음성 단말의 음성 지시를 받아 피킹·분배 작업을 수행하는 시스템." },
  { group: "PIC", code: "PIC-PAL", name: "Palletizer / Depalletizer", description: "박스나 상품을 팔레트에 자동 적재하거나 팔레트에서 자동 해체하는 설비." },
  { group: "PAC", code: "PAC-PACK", name: "Packaging", description: "상품의 포장, 제함, 봉함 등 포장 공정을 자동화하는 설비." },
  { group: "PAC", code: "PAC-LABEL", name: "Autolabeler", description: "상품 또는 포장 박스에 송장·바코드 등의 라벨을 자동 출력·부착하는 설비." },
  { group: "PAC", code: "SEN-BAR", name: "Barcode / RFID / PDA", description: "바코드·RFID·휴대용 단말기를 이용해 상품과 재고 정보를 인식·수집하는 장비." },
  { group: "PAC", code: "SEN-VIS", name: "Machine Vision / 검수", description: "카메라와 영상 분석 기술을 활용해 상품 식별, 수량·치수 확인, 품질 및 출고 검수를 수행하는 시스템." },
  // 2026-11-02 개정 — 설비 카탈로그 「보안·안전」 · 「오케스트레이션」 대분류와 맞춤(Vault 적용 카테고리도 이 두 코드에서 나온다).
  { group: "PAC", code: "SEN-SAFE", name: "보안·안전", description: "CCTV·출입통제·안전 센서 등 물류센터 보안과 작업자·설비 안전을 위한 장비·시스템." },
  { group: "SW", code: "SW-WMS", name: "WMS", description: "재고, 입고, 보관, 피킹, 출고 등 창고 운영 전반을 관리하는 시스템." },
  { group: "SW", code: "SW-WES", name: "WES", description: "창고 내 작업과 자동화 설비의 실행 순서, 작업 배분, 운영 흐름을 최적화하는 시스템." },
  { group: "SW", code: "SW-WCS", name: "WCS / ECS / 제어", description: "컨베이어·소터·자동창고 등 물류 설비의 동작과 제어 명령을 관리하는 시스템." },
  { group: "SW", code: "SW-ROBOT", name: "로봇 관제·운영 SW", description: "AMR·AGV 등 복수 로봇의 위치, 상태, 작업 배정, 경로 및 운영 상황을 관리하는 소프트웨어." },
  { group: "SW", code: "SW-AI", name: "AI·오케스트레이션", description: "AI 에이전트·의사결정·배차 최적화 등으로 여러 시스템과 설비의 작업을 조율하는 소프트웨어." },
  { group: "SERV", code: "SERV-SIM", name: "Simulation / Digital Twin", description: "물류센터와 설비 운영을 가상 환경에서 모델링하고 처리량·동선·운영 시나리오를 검증하는 기술." },
  { group: "SERV", code: "SERV-SI", name: "SI / 설계·구축", description: "복수 물류 설비·소프트웨어의 설계, 도입, 연동, 구축 및 통합을 수행하는 서비스." },
  { group: "SERV", code: "SERV-MNT", name: "유지보수", description: "물류 자동화 설비 또는 시스템의 점검, 장애 대응, 수리 및 유지관리 서비스." },
  { group: "SERV", code: "SERV-OPS", name: "물류 운영 / 3PL", description: "보관, 입출고, 재고관리, 풀필먼트 등 고객사의 물류 업무를 위탁 수행하는 서비스." },
];

const BY_CODE = new Map(PARTNER_TECH_CODES.map((c) => [c.code, c]));
export const partnerTechCode = (code: string) => BY_CODE.get(code) || null;

/** 사용 중지한 코드(DB · 관리 화면) — 목록엔 남고 새로 고르는 칸에서는 빼는 데 쓴다 */
export const INACTIVE_TECH_CODES = new Set<string>();

/**
 * 기술 분류 DB 목록(「기술 분류」 탭 · crm_tech_categories)을 이 목록에 제자리 반영 — 새 코드 추가 · 이름 · 설명 · 그룹 · 사용 여부.
 *   같은 배열 · Map 을 쓰는 화면이 그대로 새 값을 본다(techCategoryRegistry 가 앱 시작 때 부른다).
 */
export function applyPartnerTechCodes(rows: Array<{ code: string; group: string; name: string; description: string; active: boolean }>) {
  for (const r of rows) {
    const cur = BY_CODE.get(r.code);
    if (cur) Object.assign(cur, { group: r.group, name: r.name, description: r.description });
    else {
      const add: PartnerTechCode = { code: r.code, group: r.group, name: r.name, description: r.description };
      PARTNER_TECH_CODES.push(add);
      BY_CODE.set(r.code, add);
    }
    if (r.active) INACTIVE_TECH_CODES.delete(r.code); else INACTIVE_TECH_CODES.add(r.code);
  }
  const order = new Map(rows.map((r, i) => [r.code, i]));
  PARTNER_TECH_CODES.sort((a, b) => (order.get(a.code) ?? 1e6) - (order.get(b.code) ?? 1e6));
}

/**
 * 「주요 기술」(자유 입력) → 24코드 — **이름이 그대로 대응하는 것만**(사용자 확정 2026-10-27 「정확 매핑」).
 * 비교는 소문자·앞뒤 공백 제거. 여기 없는 값(Consulting · TMS · Voice · Smart Glass · Robot · Vision Picking 등)은 코드를 달지 않는다.
 * ★ 마이그레이션 20261027_partner_tech_codes.sql 의 CASE 와 같은 표다.
 */
export const TECH_EXACT_MAP: Record<string, string[]> = {
  "as/rs": ["STO-ASRS"],
  "shuttle": ["STO-SHUT"],
  "vlm": ["STO-VLM"],
  "autostore": ["STO-AUTO"],
  "amr": ["MOV-AMR"],
  "agv": ["MOV-AGV"],
  "agv/amr": ["MOV-AGV", "MOV-AMR"],
  "conveyor": ["MOV-CONV"],
  "sorter": ["SOR-SORT"],
  "mini sorter": ["SOR-SORT"],
  "dps": ["PIC-DPSDAS"],
  "das": ["PIC-DPSDAS"],
  "dpc": ["PIC-DPSDAS"],
  "picking robot": ["PIC-ROBOT"],
  "palletizer": ["PIC-PAL"],
  "depalletizer": ["PIC-PAL"],
  "packaging": ["PAC-PACK"],
  "autolabeler": ["PAC-LABEL"],
  "pda": ["SEN-BAR"],
  "rfid": ["SEN-BAR"],
  "vision": ["SEN-VIS"],
  "vision system": ["SEN-VIS"],
  "wms": ["SW-WMS"],
  "wes": ["SW-WES"],
  "digital twin": ["SERV-SIM"],
  "automation design": ["SERV-SI"],
  "total solutions": ["SERV-SI"],
  "maintenance": ["SERV-MNT"],
  "a/s": ["SERV-MNT"],
};

/** 「주요 기술」 값 목록 → 코드(중복 제거, 분류표 순서). */
export function techCodesFromTechnologies(techs: unknown): string[] {
  const list = Array.isArray(techs) ? techs : typeof techs === "string" ? techs.split(",") : [];
  const set = new Set<string>();
  list.forEach((t) => (TECH_EXACT_MAP[String(t).trim().toLowerCase()] || []).forEach((c) => set.add(c)));
  return sortTechCodes(Array.from(set));
}

/** 저장값 정리 — 분류표에 있는 코드만, 분류표 순서로. */
export function sortTechCodes(codes: unknown): string[] {
  const arr = Array.isArray(codes) ? codes.map(String) : [];
  const order = new Map(PARTNER_TECH_CODES.map((c, i) => [c.code, i]));
  return Array.from(new Set(arr.filter((c) => order.has(c)))).sort((a, b) => (order.get(a)! - order.get(b)!));
}
